import { Controller } from "@hotwired/stimulus"
import { STEPS, stepHint, stepReady, firstIncompleteStep, primaryLabel } from "../modules/product_wizard.mjs"
import { wizardLayout } from "../modules/wizard_layout.js"

// Wizard Crear / Editar producto: un solo <form> con los 4 pasos; valida con product_wizard.mjs y
// publica el estado (product-wizard:state). Doc: _docs/features/products/partner_product_form.md
export default class extends Controller {
  static targets = ["form", "header", "step", "stepItem", "stepLabel", "hint", "primary", "back", "preview", "previewSlotMobile", "previewSlotDesktop"]
  static values = {
    mode: { type: String, default: "create" },
    step: { type: Number, default: 1 }
  }

  connect() {
    this.onSubmit = event => this.#guardSubmit(event)
    this.onSubmitEnd = () => this.#submitEnded()
    this.formTarget.addEventListener("submit", this.onSubmit)
    this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.#observeLayout()
    this.#observeHeader()
    this.show(this.stepValue, { scroll: false })
  }

  disconnect() {
    this.formTarget.removeEventListener("submit", this.onSubmit)
    this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.layoutObserver?.disconnect()
    this.headerObserver?.disconnect()
  }

  get edit() {
    return this.modeValue === "edit"
  }

  // Botón principal: en creación avanza o guarda en el último paso; en edición guarda si 1–3 están completos
  next() {
    const state = this.state()
    if (this.edit) {
      if (firstIncompleteStep(state)) return
      return this.save()
    }
    if (!stepReady(this.stepValue, state)) return
    if (this.stepValue === STEPS.length) {
      const pending = firstIncompleteStep(state)
      return pending ? this.show(pending) : this.save()
    }
    this.show(this.stepValue + 1)
  }

  back() {
    if (this.stepValue > 1) this.show(this.stepValue - 1)
  }

  // Pestañas en edición, pasos ya completados en creación y los "Editar" de la revisión
  goTo(event) {
    const step = Number(event.params.step)
    if (this.#canOpen(step)) this.show(step)
  }

  show(step, { scroll = true } = {}) {
    this.stepValue = step
    this.element.dataset.step = step
    this.stepTargets.forEach(section => { section.hidden = Number(section.dataset.step) !== step })
    this.stepItemTargets.forEach(item => {
      const n = Number(item.dataset.step)
      item.classList.toggle("is-current", n === step)
      item.classList.toggle("is-done", !this.edit && n < step)
      const button = item.querySelector("button")
      if (!button) return
      if (this.edit) {
        button.setAttribute("aria-selected", n === step)
      } else {
        n === step ? button.setAttribute("aria-current", "step") : button.removeAttribute("aria-current")
        button.setAttribute("aria-disabled", !this.#canOpen(n))
      }
    })
    if (this.hasStepLabelTarget) this.stepLabelTarget.textContent = `Paso ${step} de ${STEPS.length}`
    this.backTargets.forEach(button => { button.hidden = this.edit || step === 1 })
    if (scroll) window.scrollTo({ top: 0 })
    this.refresh()
  }

  // Un mismo cambio llega varias veces: se agrupa en un microtask y se repinta una vez
  refresh() {
    if (this.refreshQueued) return
    this.refreshQueued = true
    queueMicrotask(() => {
      this.refreshQueued = false
      if (this.element.isConnected) this.#paint()
    })
  }

  // Re-evalúa validez, aviso, botón y estado publicado
  #paint() {
    const state = this.state()
    const pending = this.edit ? firstIncompleteStep(state) : this.stepValue
    const hint = pending ? stepHint(pending, state) : ""
    const ready = this.edit ? !pending : stepReady(this.stepValue, state)
    this.hintTargets.forEach(element => {
      element.textContent = hint
      element.hidden = !hint
    })
    this.primaryTargets.forEach(button => {
      // Un snapshot de Turbo puede traer el botón en "Guardando…"
      if (!this.submitting) {
        button.disabled = false
        button.textContent = primaryLabel(this.modeValue, this.stepValue)
      }
      button.setAttribute("aria-disabled", !ready)
    })
    this.dispatch("state", { detail: { state } })
  }

  // Estado del formulario leído del DOM
  state() {
    const form = this.formTarget
    const value = selector => form.querySelector(selector)?.value?.trim() || ""
    const photo = form.querySelector("[data-controller~='photo-field']")
    const section = form.querySelector("input[name='product[section_id]']")
    const sectionLabel = form.querySelector("[data-choice-sheet-target='label']")
    const discount = form.querySelector("[data-controller~='discount-form']")?.dataset || {}
    const stock = form.querySelector("[data-controller~='stock-field']")?.dataset || {}
    const options = form.querySelector("[data-controller~='product-options']")?.dataset || {}
    return {
      name: value("input[name='product[name]']"),
      // HTML que Trix escribe en su hidden; customer-preview lo filtra
      description: value("input[name='product[description]']"),
      photo: photo?.dataset.photoFieldStateValue || "empty",
      photoUrl: photo?.querySelector("img")?.getAttribute("src") || "",
      section: section?.value || "",
      sectionLabel: section?.value ? (sectionLabel?.textContent.trim() || "") : "",
      categories: [...form.querySelectorAll("input[name='product[category_ids][]']:checked")].map(input => input.dataset.label),
      price: value("input[name='product[price]']"),
      // Lo deriva discount-form en cada cambio
      discountError: discount.discountError || "",
      discount: {
        active: discount.discountActive === "true",
        percent: Number(discount.discountPercent) || 0,
        final: discount.discountFinal === undefined || discount.discountFinal === "" ? null : Number(discount.discountFinal)
      },
      stock: stock.stockActive === "true" && stock.stockQuantity !== "" ? Number(stock.stockQuantity) : null,
      hasOptions: this.#hasOptions(),
      optionsError: options.optionsError || "",
      options: options.optionsState ? JSON.parse(options.optionsState) : null
    }
  }

  save() {
    this.submitting = true
    this.primaryTargets.forEach(button => {
      button.disabled = true
      button.textContent = "Guardando…"
    })
    this.formTarget.requestSubmit()
  }

  // Enter dentro de un input no manda el form
  #guardSubmit(event) {
    if (this.submitting) return
    event.preventDefault()
    this.next()
  }

  // Turbo terminó y seguimos acá: hubo error de red
  #submitEnded() {
    this.submitting = false
    this.primaryTargets.forEach(button => { button.disabled = false })
    this.refresh()
  }

  // Dos radios ("tal cual" / "opciones"); sin elección queda null y el paso 3 lo pide
  #hasOptions() {
    const radio = this.formTarget.querySelector("input[type='radio'][name='product[requires_ingredients]']:checked")
    return radio ? radio.value === "1" : null
  }

  #canOpen(step) {
    return this.edit || step < this.stepValue
  }

  // La vista del cliente (mismo nodo) se mueve entre la columna derecha (wide) y el paso Revisión
  #placePreview() {
    if (!this.hasPreviewTarget || !this.hasPreviewSlotMobileTarget || !this.hasPreviewSlotDesktopTarget) return
    const slot = wizardLayout(this.element) === "wide" ? this.previewSlotDesktopTarget : this.previewSlotMobileTarget
    if (this.previewTarget.parentElement !== slot) slot.appendChild(this.previewTarget)
  }

  // El nivel lo decide CSS por el ancho del contenedor; mover el nodo se posterga un tick (loop del observer)
  #observeLayout() {
    this.#placePreview()
    if (typeof ResizeObserver === "undefined") return
    this.layoutObserver = new ResizeObserver(() => {
      if (this.placeQueued) return
      this.placeQueued = true
      setTimeout(() => {
        this.placeQueued = false
        if (this.element.isConnected) this.#placePreview()
      }, 0)
    })
    this.layoutObserver.observe(this.element)
  }

  // La vista del cliente (desktop) se pega bajo la cabecera: su alto cambia con el aviso.
  #observeHeader() {
    if (!this.hasHeaderTarget || typeof ResizeObserver === "undefined") return
    this.headerObserver = new ResizeObserver(entries => {
      const height = Math.round(entries[0]?.contentRect.height || 0)
      this.element.style.setProperty("--wizard-header-h", `${height}px`)
    })
    this.headerObserver.observe(this.headerTarget)
  }
}
